import { useEffect, useRef, useState } from "react";
import styles from "./custom-input-number.module.scss";

const sanitizeOnce = (s) => {
	const only = (s || "").replace(/[^0-9,]/g, "");
	const parts = only.split(",");
	return parts[0] + (parts.length > 1 ? "," + parts.slice(1).join("") : "");
};

const sanitizeBefore = (s) => sanitizeOnce(s);
const sanitizeFull = (s) => sanitizeOnce(s);

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const formatLive = (san) => {
	if (!san) return "";
	const trailingComma = san.endsWith(",");
	const [i = "", d = ""] = san.split(",");
	const intDigits = i.replace(/^0+(?=\d)/, "") || "0";
	const grouped = groupThousands(intDigits);
	if (trailingComma) return `${grouped},`;
	if (san.includes(",")) {
		if (d === "") return `${grouped},`;
		if (/^0+$/.test(d)) return grouped;
		return `${grouped},${d}`;
	}
	return grouped;
};

const toNumber = (display) => {
	if (!display) return null;
	const n = Number(display.replace(/\./g, "").replace(",", "."));
	return Number.isFinite(n) ? n : null;
};

const numberToDisplay = (n) => {
	if (n === null || n === undefined || Number.isNaN(n)) return "";
	const s = String(n);
	const [iRaw, dRaw = ""] = s.split(".");
	const intPart = iRaw.replace(/\D/g, "");
	if (!dRaw || /^0+$/.test(dRaw)) return groupThousands(intPart);
	return `${groupThousands(intPart)},${dRaw}`;
};

const caretInGroupedByIntDigits = (grouped, intDigitsBefore) => {
	if (intDigitsBefore <= 0) return 0;
	let digitsCount = 0;
	for (let i = 0; i < grouped.length; i++) {
		if (grouped[i] !== ".") digitsCount++;
		if (digitsCount === intDigitsBefore) return i + 1;
	}
	return grouped.length;
};

const CustomInputNumber = ({
	id,
	name,
	label,
	value,
	onChange,
	placeholder = "",
	required = false,
	disabled = false,
	error = "",
	helper = "",
	size = "md",
	col = 12,
	autoFocus = false,
	className,
	style,
}) => {
	const stableIdRef = useRef(id || `${name || "num"}-${Math.random().toString(36).slice(2)}`);
	const inputId = stableIdRef.current;
	const inputRef = useRef(null);

	const [display, setDisplay] = useState(numberToDisplay(value));
	const isFocusedRef = useRef(false);

	useEffect(() => {
		if (isFocusedRef.current) return;
		setDisplay(numberToDisplay(value));
	}, [value]);

	const colClass = col === 6 ? styles.col6 : styles.col12;
	const describedBy = error ? `${inputId}-error` : helper ? `${inputId}-helper` : undefined;

	const handleKeyDown = (e) => {
		if (e.ctrlKey || e.metaKey) return;
		const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End", "Enter"];
		if (allowed.includes(e.key)) return;
		if (e.key >= "0" && e.key <= "9") return;
		if (e.key === ",") {
			if (display.includes(",")) e.preventDefault();
			return;
		}
		e.preventDefault();
	};

	const handleChange = (e) => {
		const raw = e.target.value;
		const rawCaret = e.target.selectionStart ?? raw.length;

		const rawBefore = raw.slice(0, rawCaret);
		const sanBefore = sanitizeBefore(rawBefore);
		const san = sanitizeFull(raw);

		const formatted = formatLive(san);
		setDisplay(formatted);
		if (onChange) {
			onChange(toNumber(formatted));
		}

		const hadCommaBefore = sanBefore.includes(",");
		const trailingCommaBefore = sanBefore.endsWith(",");
		const [iBefore = "", dBefore = ""] = sanBefore.split(",");

		const [iSan = ""] = san.split(",");
		const groupedInt = groupThousands((iSan || "0").replace(/^0+(?=\d)/, "") || "0");
		const hasCommaFormatted = formatted.includes(",");
		const commaIndex = hasCommaFormatted ? formatted.indexOf(",") : -1;

		let newCaret = 0;

		if (!hadCommaBefore) {
			const intDigitsBefore = iBefore.length;
			newCaret = caretInGroupedByIntDigits(groupedInt, intDigitsBefore);
		} else if (trailingCommaBefore) {
			if (formatted.endsWith(",")) {
				newCaret = formatted.length;
			} else if (hasCommaFormatted) {
				newCaret = commaIndex + 1;
			} else {
				newCaret = groupedInt.length;
			}
		} else {
			if (hasCommaFormatted) {
				const intDigitsBefore = iBefore.length;
				const posInInt = caretInGroupedByIntDigits(groupedInt, intDigitsBefore);
				const decKept = formatted.slice(commaIndex + 1);
				const decBefore = Math.min(dBefore.length, decKept.length);
				newCaret = posInInt + 1 + decBefore;
			} else {
				newCaret = groupedInt.length;
			}
		}

		requestAnimationFrame(() => {
			if (inputRef.current) {
				inputRef.current.setSelectionRange(newCaret, newCaret);
			}
		});
	};

	const handleFocus = () => {
		isFocusedRef.current = true;
	};

	const handleBlur = () => {
		isFocusedRef.current = false;
		const n = toNumber(display);
		const finalDisplay = numberToDisplay(n);
		setDisplay(finalDisplay);
	};

	return (
		<div className={`${styles.field} ${colClass} ${className || ""}`} style={style}>
			{label ? (
				<label htmlFor={inputId} className={styles.label}>
					{label} {required ? <span className={styles.req}>*</span> : null}
				</label>
			) : null}

			<input
				ref={inputRef}
				id={inputId}
				name={name}
				type="text"
				inputMode="decimal"
				autoComplete="off"
				autoCorrect="off"
				spellCheck={false}
				className={`${styles.input} ${styles[size]} ${error ? styles.inputError : ""}`}
				value={display}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				onBlur={handleBlur}
				placeholder={placeholder}
				required={required}
				disabled={disabled}
				autoFocus={autoFocus}
				aria-invalid={!!error || undefined}
				aria-describedby={describedBy}
			/>

			{error ? (
				<div id={`${inputId}-error`} className={styles.error}>
					{error}
				</div>
			) : helper ? (
				<div id={`${inputId}-helper`} className={styles.helper}>
					{helper}
				</div>
			) : null}
		</div>
	);
};

export default CustomInputNumber;
